<template>
  <n-card>
    <new-client-dialog v-model="isNewClientDialogOpened" @created="clientCreated" />
    <template v-if="!isFetchingClients">
      <v-card-text>
        <v-row no-gutters justify="center" align="center">
          <v-col>
            <v-row no-gutters justify="center" align="center">
              <v-col>
                <v-text-field
                  v-model="searchString"
                  placeholder="Rechercher un client..."
                  prepend-inner-icon="search"
                  hide-details
                  dense
                  clearable
                  outlined
                ></v-text-field>
              </v-col>
              <v-btn dark color="primary" elevation="0" class="ml-4" @click="isNewClientDialogOpened = true" v-if="canAddClient">
                <v-icon left>add</v-icon>
                Nouveau
              </v-btn>
            </v-row>
            <v-row no-gutters justify="center" align="center" class="pt-10 pb-6" v-if="!filteredClients.length">
              Aucun résultat
            </v-row>
            <template v-else>
              <div class="pt-4 pb-2">
                <v-row no-gutters justify="center" align="center">
                  <h3 class="pl-2">{{ filteredClients.length }} {{ `client` | pluralize(filteredClients.length) }} {{ `trouvé` | pluralize(filteredClients.length) }}</h3>
                  <v-spacer />
                  <div>
                    <v-row no-gutters justify="center" align="center">
                      <v-btn class="n-white-btn n-small-btn n-icon-btn primary--text" @click="previousPage">
                        <v-icon>chevron_left</v-icon>
                      </v-btn>
                      <div class="ml-2 primary--text font-weight-bold">
                        {{ currentPage + 1 }} / {{ nbPages }}
                      </div>
                      <v-btn class="n-white-btn n-small-btn n-icon-btn primary--text ml-2" @click="nextPage">
                        <v-icon>chevron_right</v-icon>
                      </v-btn>
                    </v-row>
                  </div>
                </v-row>
              </div>
              <n-card depressed v-for="client in filteredClientsPaginated" :key="client.id" @click="chooseClient(client)" class="my-4 clickable">
                <v-row no-gutters justify="center" align="center">
                  <v-col class="py-2 px-4">
                    <div class="font-weight-bold">{{ client.name }}</div>
                    <div v-if="client.address">{{ client.address }}</div>
                    <div>
                      <span class="pr-2" v-if="client.postalCode">{{ client.postalCode }}</span>
                      <span v-if="client.city">{{ client.city }}</span>
                    </div>
                  </v-col>
                  <v-divider vertical />
                  <div class="py-2 px-4">
                    <v-icon>chevron_right</v-icon>
                  </div>
                </v-row>
              </n-card>
            </template>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

    </template>
    <template v-else>
      <v-row no-gutters justify="center" align="center" class="my-5">
        <v-progress-circular indeterminate color="primary darken-1"></v-progress-circular>
      </v-row>
    </template>
  </n-card>
</template>

<script>
import _ from 'lodash';
import { mapState } from 'vuex';
import NewClientDialog from '@/components/NewClientDialog.vue';

const PAGE_SIZE = 25;

export default {
  data: () => ({
    selectedClientId: null,
    isNewClientDialogOpened: false,
    isSubmiting: false,
    searchString: ``,
    isFetchingClients: false,
    currentPage: 0,
  }),
  computed: {
    ...mapState(`model/client`, [
      `company`,
      `isMaker`,
    ]),
    ...mapState(`model/user`, [
      `me`,
      `isMakerUser`,
    ]),
    ...mapState(`ui/newIntervention`, [
      `client`,
    ]),
    ...mapState(`ui/newIntervention`, {
      clients: `dbClients`,
    }),
    orderedClients() {
      return _.chain(this.clients)
        .map(client => ({
          ...client,
          $name: _.toLower(client.name),
          $searchString: _.toLower(`
            ${client.name} ${client.address || ``} ${client.postalCode || ``} ${client.city || ``}
          `),
        }))
        .orderBy(`$name`)
        .value();
    },
    filteredClients() {
      let clients = this.orderedClients;
      if (_.trim(this.searchString) !== ``) {
        const searchString = _.lowerCase(this.searchString);
        clients = _.filter(clients, client =>
          client.$searchString.indexOf(searchString) !== -1
        );
      }
      return clients;
    },
    filteredClientsPaginated() {
      const firstIndex = this.currentPage * PAGE_SIZE;
      return this.filteredClients.slice(firstIndex, firstIndex + PAGE_SIZE);
    },
    nbPages() {
      return Math.ceil(this.filteredClients.length / PAGE_SIZE);
    },
    canAddClient() {
      return Boolean(
        this.me
        && this.company.isRoot
        && [`aba`, `sushi-robots`, `techni-froid-cuisines`].indexOf(this.company.slug) === -1
        && (this.me.clientAdmin || this.me.isIndego)
      );
    },
  },
  methods: {
    previousPage() {
      if (this.currentPage > 0) {
        this.currentPage -= 1;
      }
    },
    nextPage() {
      const newPageIndex = (this.currentPage + 1) * PAGE_SIZE;
      if (newPageIndex < this.filteredClients.length) {
        this.currentPage += 1;
      }
    },
    async fetchClients() {
      this.isFetchingClients = true;
      await this.$store.dispatch(`ui/newIntervention/fetchClients`);
      this.$emit(`ready`);
      if (this.isMaker && !this.isMakerUser) {
        this.chooseClient(this.orderedClients[0]);
      }
      this.isFetchingClients = false;
    },
    chooseClient(client) {
      this.selectedClientId = client.id;
      return this.submit();
    },
    async clientCreated(client) {
      this.isNewClientDialogOpened = false;
      await this.fetchClients();
      const choosenClient = _.find(this.clients, { id: client.id });
      this.chooseClient(choosenClient);
    },
    submit() {
      if (this.isSubmiting) { return; }
      this.isSubmiting = true;
      this.autocompleteError = false;
      this.autocompleteReadonly = false;
      this.autocompleteSuccess = false;
      if (_.isNaN(parseInt(this.selectedClientId, 10))) {
        setTimeout(() => { this.autocompleteError = true; });
        this.isSubmiting = false;
        return;
      }
      this.autocompleteReadonly = true;
      this.autocompleteSuccess = true;
      const client = _.find(this.clients, { id: this.selectedClientId });
      this.$store.commit(`ui/newIntervention/SET_CLIENT`, client);
      this.$emit(`choose`, client);
    },
  },
  watch: {
    clients(clients) {
      if (this.client && this.client.id) {
        const client = _.find(this.clients, { id: this.client.id });
        if (client) {
          this.selectedClientId = client.id;
          this.$store.commit(`ui/newIntervention/SET_CLIENT`, client);
          return this.$emit(`choose`, client);
        }
      }

      if (!this.isMaker && clients.length === 1) {
        this.selectedClientId = clients[0].id;
        const client = clients[0];
        this.$store.commit(`ui/newIntervention/SET_CLIENT`, client);
        this.$emit(`choose`, client);
      }

      return null;
    },
    selectedClientId() { this.autocompleteError = false; },
    searchString() {
      this.currentPage = 0;
    },
    searchTags() {
      this.currentPage = 0;
    },
  },
  beforeMount() {
    this.fetchClients();
  },
  components: {
    NewClientDialog,
  },
};
</script>
